import React, { useEffect, useState } from "react";
import {
  Card,
  Descriptions,
  Image,
  Table,
  Divider,
  Typography,
  Row,
  Col,
  Tag,
  Space,
} from "antd";
import { fetchImage } from "../../views/common/downloadUploadImage";
import moment from "moment";
import { ConvertFirstLatterToCapital } from "../../utility";
import { formatStatus } from "./applicationListTable";
import { FilePdfOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const ApplicationDetailDynamic = ({ data }) => {
  const { result = {}, schema = { properties: {} } } = data || {};
  const { dynamicData = {}, uploadedDocuments = [] } = result || {};
  const MAX_LENGTH = 100; // characters before truncating
  const isSectionedSchema = Array.isArray(schema?.sections);

  // State to hold loaded image URLs
  const [images, setImages] = useState({
    applicantImage: null,
    aadharCardImage: null,
    panCardImage: null,
  });

  const [files, setFiles] = useState({});
  useEffect(() => {
    const loadFiles = async () => {
      const fileData = {};

      for (const doc of uploadedDocuments) {
        const key = doc.fieldName;
        const path = doc.path;

        const fileURL = await fetchImage(path);

        if (!fileURL) {
          fileData[key] = null;
          continue;
        }

        // detect type from extension
        const ext = path.split(".").pop().toLowerCase();
        const isPdf = ext === "pdf";

        fileData[key] = {
          url: fileURL,
          type: isPdf ? "pdf" : "image",
          name: path.split("/").pop(), // optional: file name
        };
      }

      setFiles(fileData);
    };

    loadFiles();

    // cleanup object URLs
    return () => {
      Object.values(files).forEach((file) => {
        if (file?.url) URL.revokeObjectURL(file.url);
      });
    };
  }, [uploadedDocuments]);

  const [expanded, setExpanded] = useState(false);
  const renderFieldItem = ([key, config]) => {
    if (config.readOnly) return null;
    const title = config.title || key;
    const value = dynamicData[key];
    if (value === null || value === undefined || value === "") return null;

    // Special handling for long text
    if (typeof value === "string" && value.length > MAX_LENGTH) {
      return (
        <Descriptions.Item
          key={key}
          label={title}
          labelStyle={{ width: 220, fontWeight: 500 }}
          contentStyle={{ whiteSpace: "normal" }}
        >
          <div>
            {expanded ? value : `${value.slice(0, MAX_LENGTH)}...`}
            <a
              onClick={() => setExpanded(!expanded)}
              style={{
                marginLeft: 8,
                fontSize: 13,
                fontWeight: 500,
                color: "#FF8744", // Ant Design primary blue
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {expanded ? "Show less" : "Read more"}
            </a>
          </div>
        </Descriptions.Item>
      );
    }

    // Boolean
    if (config.type === "boolean") {
      return (
        <Descriptions.Item
          key={key}
          label={title}
          labelStyle={{ width: 220, fontWeight: 500 }}
          contentStyle={{ whiteSpace: "normal" }}
        >
          {value ? "Yes" : "No"}
        </Descriptions.Item>
      );
    }
    // Arrays
    if (config.type === "array" && Array.isArray(value)) {
      const itemSchema = config.items?.properties || {};
      const columns = Object.entries(itemSchema).map(([colKey, colConfig]) => ({
        title: colConfig.title || colKey,
        dataIndex: colKey,
        key: colKey,
      }));

      return (
        <Descriptions.Item
          key={key}
          label={title}
          labelStyle={{ width: 220, fontWeight: 500 }}
          contentStyle={{ whiteSpace: "normal" }}
          span={2}
        >
          <Table
            columns={columns}
            dataSource={value.map((item, i) => ({ ...item, key: i }))}
            pagination={false}
            size="small"
            bordered
          />
        </Descriptions.Item>
      );
    }
    //object
    if (config.type === "object" && value && typeof value === "object") {
      const itemSchema = config.items?.properties || config.properties || {};
      const columns = Object.entries(itemSchema).map(([colKey, colConfig]) => ({
        title: colConfig.title || colKey,
        dataIndex: colKey,
        key: colKey,
      }));

      return (
        <Descriptions.Item
          key={key}
          label={title}
          labelStyle={{ width: 220, fontWeight: 500 }}
          contentStyle={{ whiteSpace: "normal" }}
          span={2}
        >
          <Table
            columns={columns}
            dataSource={[{ ...value, key: 0 }]} // wrap object in an array so Table can render
            pagination={false}
            size="small"
            bordered
          />
        </Descriptions.Item>
      );
    }
    if (config.fieldType === "date" && value) {
      return (
        <Descriptions.Item
          key={key}
          label={title}
          labelStyle={{ width: 220, fontWeight: 500 }}
          contentStyle={{ whiteSpace: "normal" }}
        >
          {dayjs(value).isValid()
            ? dayjs(value).format("DD MMM YYYY")
            : value}
        </Descriptions.Item>
      );
    }
    // Default
    return (
      <Descriptions.Item
        key={key}
        label={title}
        labelStyle={{ width: 220, fontWeight: 500 }}
        contentStyle={{ whiteSpace: "normal" }}
      >
        {value}
      </Descriptions.Item>
    );
  };
  const renderSection = (section) => {
    const isSelfDeclaration = section.title === "Self Declaration";

    return (
      <Card
        key={section.title}
        size="small"
        title={section.title}
        style={{ marginBottom: 16 }}
      >
        <Descriptions
          bordered
          column={isSelfDeclaration ? 1 : 2} // 👈 force single column for Self Declaration
          size="middle"
          className={isSelfDeclaration ? "self-declaration-desc" : ""}
        >
          {Object.entries(section.properties).map(renderFieldItem)}
        </Descriptions>
      </Card>
    );
  };

  const formatLabel = (key) => {
    return key
      .replace(/([A-Z])/g, " $1") // camelCase → space
      .replace(/_/g, " ") // snake_case → space
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize every word
  };
  return (
    <Card style={{ maxWidth: "100%" }}>
      {/* TOP: Status, Amount, Timeline */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Space size="large">
            <span className="commonSmallFont commonFontFamily">Status:</span>
            <Tag color={result.status === "approved" ? "green" : "blue"}>
              {formatStatus(result.status)}
            </Tag>
            {result?.amountRequired ? (
              <span className="commonSmallFont commonFontFamily">
                Amount Required: ₹{result.amountRequired.toLocaleString()}
              </span>
            ) : null}

            <span className="commonSmallFont commonFontFamily">
              Created At: {moment(result.timeline).format("DD MMM YYYY")}
            </span>
          </Space>
        </Col>
      </Row>

      {/* MAIN: Left - Details | Right - Passport Image */}
      <Row gutter={24}>
        <Col xs={24} md={files.applicantImage ? 20 : 24}>
          {isSectionedSchema ? (
            schema.sections
              .filter((section) => section.title !== "Documents Required") // ✅ hide this section
              .map(renderSection)
          ) : (
            <Descriptions
              bordered
              column={2}
              size="middle"
              style={{ marginBottom: 24 }}
            >
              {Object.entries(schema.properties)
                .filter(
                  ([key]) =>
                    ![
                      "applicantImage",
                      "aadharCardImage",
                      "panCardImage",
                    ].includes(key)
                )
                .map(renderFieldItem)}
            </Descriptions>
          )}
        </Col>

        <Col xs={24} md={4}>
          {files.applicantImage && (
            <div style={{ textAlign: "center" }}>
              <div className="commonSmallFont commonFontFamily">
                Applicant Image
              </div>
              {files.applicantImage.type === "pdf" ? (
                <a
                  href={files.applicantImage.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <Card
                    hoverable
                    style={{
                      width: 150,
                      height: 135,
                      borderRadius: 8,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                    }}
                    bodyStyle={{ padding: 0, textAlign: "center" }}
                  >
                    <FilePdfOutlined
                      style={{
                        fontSize: 48,
                        color: "#d32f2f",
                        marginBottom: 8,
                      }}
                    />
                    <span
                      style={{ fontSize: 12, fontWeight: 500, color: "#555" }}
                    >
                      View PDF
                    </span>
                  </Card>
                </a>
              ) : (
                <Image
                  width={150}
                  height={200}
                  style={{
                    objectFit: "cover",
                    border: "1px solid #d9d9d9",
                    borderRadius: 4,
                  }}
                  src={files.applicantImage.url}
                  alt="Applicant"
                  preview
                />
              )}
            </div>
          )}
        </Col>
      </Row>

      {/* BOTTOM: Aadhar & PAN */}
      {/* {(files.aadharCardImage || files.panCardImage) && (
        <>
          <Divider />
          <Row gutter={24} justify="start">
            {files.aadharCardImage && (
              <Col>
                <div className="commonSmallFont commonFontFamily">
                  Aadhar Card
                </div>
                {files.aadharCardImage.type === "pdf" ? (
                  <a
                    href={files.aadharCardImage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Card
                      hoverable
                      style={{
                        width: 150,
                        height: 135,
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                      }}
                      bodyStyle={{ padding: 0, textAlign: "center" }}
                    >
                      <FilePdfOutlined
                        style={{
                          fontSize: 48,
                          color: "#d32f2f",
                          marginBottom: 8,
                        }}
                      />
                      <span
                        style={{ fontSize: 12, fontWeight: 500, color: "#555" }}
                      >
                        View PDF
                      </span>
                    </Card>
                  </a>
                ) : (
                  <Image
                    width={200}
                    src={files.aadharCardImage.url}
                    alt="Aadhar Card"
                    style={{ borderRadius: 4 }}
                    preview
                  />
                )}
              </Col>
            )}

            {files.panCardImage && (
              <Col>
                <div className="commonSmallFont commonFontFamily">PAN Card</div>
                {files.panCardImage.type === "pdf" ? (
                  <a
                    href={files.panCardImage.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none" }}
                  >
                    <Card
                      hoverable
                      style={{
                        width: 150,
                        height: 135,
                        borderRadius: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                      }}
                      bodyStyle={{ padding: 0, textAlign: "center" }}
                    >
                      <FilePdfOutlined
                        style={{
                          fontSize: 48,
                          color: "#d32f2f",
                          marginBottom: 8,
                        }}
                      />
                      <span
                        style={{ fontSize: 12, fontWeight: 500, color: "#555" }}
                      >
                        View PDF
                      </span>
                    </Card>
                  </a>
                ) : (
                  <Image
                    width={200}
                    src={files.panCardImage.url}
                    alt="PAN Card"
                    style={{ borderRadius: 4 }}
                    preview
                  />
                )}
              </Col>
            )}
          </Row>
        </>
      )} */}
      {/* Dynamic File Preview except applicantImage */}
      {files && Object.keys(files).length > 0 && (
        <>
          <Divider />
          <Row gutter={24} justify="start">
            {Object.entries(files).map(([key, file]) => {
              // Skip applicantImage
              if (key === "applicantImage" || !file?.url) return null;

              const label = formatLabel(key);

              return (
                <Col key={key}>
                  <div className="commonSmallFont commonFontFamily">
                    {label}
                  </div>
                  {file.type === "pdf" ? (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: "none" }}
                    >
                      <Card
                        hoverable
                        style={{
                          width: 150,
                          height: 135,
                          borderRadius: 8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
                        }}
                        bodyStyle={{ padding: 0, textAlign: "center" }}
                      >
                        <FilePdfOutlined
                          style={{
                            fontSize: 48,
                            color: "#d32f2f",
                            marginBottom: 8,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          View PDF
                        </span>
                      </Card>
                    </a>
                  ) : (
                    <Image
                      width={200}
                      src={file.url}
                      alt={label}
                      style={{ borderRadius: 4 }}
                      preview
                    />
                  )}
                </Col>
              );
            })}
          </Row>
        </>
      )}
    </Card>
  );
};

export default ApplicationDetailDynamic;
